import { Text, VStack } from "@chakra-ui/react"
import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"

interface Summary {
  hydrogen_consumed: number
  hydrogen_ordered: number
  hydrogen_returned: number
  returned_percentage: number
  hydrogen_planned: number
  scheduled_fulfillments: number
  avg_per_scheduled_fulfillment: number
  completed_fulfillments: number
  avg_per_completed_fulfillment: number
  avg_consumed_per_fulfillment: number
}

interface CustomerPerformanceSummaryProps {
  summary: Summary
  loading?: boolean
}

export function CustomerPerformanceSummarySkeleton() {
  return (
    <FigureGrid>
      {[1, 2, 3, 4].map((i) => (
        <Figure key={i} heading="Loading..." loading />
      ))}
    </FigureGrid>
  )
}

function formatQuantity(value: number): string {
  return Math.round(value).toLocaleString()
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}

export default function CustomerPerformanceSummary({ summary, loading = false }: CustomerPerformanceSummaryProps) {
  if (loading) {
    return <CustomerPerformanceSummarySkeleton />
  }

  const consumedDisplay = formatQuantity(summary.hydrogen_consumed)
  const orderedDisplay = formatQuantity(summary.hydrogen_ordered)
  const returnedDisplay = formatQuantity(summary.hydrogen_returned)
  const returnedPercentageDisplay = formatPercentage(summary.returned_percentage)
  const plannedDisplay = formatQuantity(summary.hydrogen_planned)
  const avgPerScheduledDisplay = formatQuantity(summary.avg_per_scheduled_fulfillment)
  const avgPerCompletedDisplay = formatQuantity(summary.avg_per_completed_fulfillment)
  const avgConsumedDisplay = formatQuantity(summary.avg_consumed_per_fulfillment)

  return (
    <FigureGrid>
      <Figure heading="H₂ Consumed">
        <VStack align="start" gap={1}>
          <Text fontSize="3xl" fontWeight="bold" color="primary">
            {consumedDisplay}{" "}
            <Text as="span" fontSize="lg">
              kg
            </Text>
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {orderedDisplay} kg ordered
          </Text>
        </VStack>
      </Figure>

      <Figure heading="H₂ Returned">
        <VStack align="start" gap={1}>
          <Text fontSize="3xl" fontWeight="bold" color="primary">
            {returnedDisplay}{" "}
            <Text as="span" fontSize="lg">
              kg
            </Text>
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {returnedPercentageDisplay} of ordered
          </Text>
        </VStack>
      </Figure>

      <Figure heading="H₂ Planned">
        <VStack align="start" gap={1}>
          <Text fontSize="3xl" fontWeight="bold" color="primary">
            {plannedDisplay}{" "}
            <Text as="span" fontSize="lg">
              kg
            </Text>
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {summary.scheduled_fulfillments} fulfillments • {avgPerScheduledDisplay} kg avg
          </Text>
        </VStack>
      </Figure>

      <Figure heading="H₂ per Fulfillment">
        <VStack align="start" gap={1}>
          <Text fontSize="3xl" fontWeight="bold" color="primary">
            {avgPerCompletedDisplay}{" "}
            <Text as="span" fontSize="lg">
              kg
            </Text>
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {avgConsumedDisplay} kg consumed avg
          </Text>
        </VStack>
      </Figure>
    </FigureGrid>
  )
}

